(() => {
  const cartAutoUpdate = window.MXCartAutoUpdate;

  if (!cartAutoUpdate) {
    return;
  }

  const {
    CONFIG,
    SELECTORS,
    parseMin,
    clampQuantity,
    isQuantityReadyForSubmit,
    syncDecreaseButtonState,
    hasFocusedQuantityInput,
    getQuantityInputFromActionButton,
    syncTerminal,
    syncFormDetails,
    getCartPageFromResponse,
    postCartUpdate,
  } = cartAutoUpdate;

  const createCartFormController = (form) => {
    if (form.dataset.cartAutoUpdateReady === 'true') {
      return;
    }

    form.dataset.cartAutoUpdateReady = 'true';

    let updateTimer;
    let isUpdating = false;
    let hasPendingUpdate = false;
    let hasDeferredUpdateWhileEditing = false;

    const scheduleSubmit = (delay = CONFIG.autoUpdateDelayMs) => {
      window.clearTimeout(updateTimer);
      updateTimer = window.setTimeout(() => {
        submitUpdate();
      }, delay);
    };

    const patchCartContent = async () => {
      const currentCartPage = document.querySelector(SELECTORS.cartPage);

      if (!(currentCartPage instanceof HTMLElement)) {
        throw new Error('Current cart page root not found.');
      }

      const nextCartPage = await getCartPageFromResponse();

      await syncTerminal(currentCartPage, nextCartPage);

      const syncedWithoutReplace = syncFormDetails(
        currentCartPage,
        nextCartPage,
      );

      if (syncedWithoutReplace) {
        const updatedInputs = currentCartPage.querySelectorAll(
          SELECTORS.quantityInput,
        );

        updatedInputs.forEach((updatedInput) => {
          if (updatedInput instanceof HTMLInputElement) {
            syncDecreaseButtonState(updatedInput);
          }
        });
      }

      if (!syncedWithoutReplace) {
        const currentDynamicArea = currentCartPage.querySelector(
          `${SELECTORS.cartForm}, ${SELECTORS.cartEmptyState}`,
        );
        const nextDynamicArea = nextCartPage.querySelector(
          `${SELECTORS.cartForm}, ${SELECTORS.cartEmptyState}`,
        );

        if (
          !(currentDynamicArea instanceof HTMLElement) ||
          !(nextDynamicArea instanceof HTMLElement)
        ) {
          throw new Error('Cart dynamic area not found.');
        }

        currentDynamicArea.replaceWith(nextDynamicArea);
        setupCartAutoUpdate(currentCartPage);
      }
    };

    const submitUpdate = async () => {
      if (hasFocusedQuantityInput(form)) {
        hasDeferredUpdateWhileEditing = true;
        return;
      }

      if (isUpdating) {
        hasPendingUpdate = true;
        return;
      }

      isUpdating = true;
      form.classList.add('is-updating');

      try {
        await postCartUpdate(form);
        await patchCartContent();
      } catch (error) {
        // Fallback to normal submit if AJAX update is not available.
        form.submit();
        return;
      } finally {
        isUpdating = false;

        if (form.isConnected) {
          form.classList.remove('is-updating');
        }
      }

      if (hasPendingUpdate) {
        hasPendingUpdate = false;
        scheduleSubmit();
      }
    };

    const initializeQuantityInputs = () => {
      const quantityInputs = form.querySelectorAll(SELECTORS.quantityInput);

      quantityInputs.forEach((quantityInput) => {
        if (quantityInput instanceof HTMLInputElement) {
          syncDecreaseButtonState(quantityInput);
        }
      });
    };

    const handleFormSubmit = (event) => {
      const submitter = event.submitter;

      if (
        submitter instanceof HTMLButtonElement &&
        submitter.name === 'checkout'
      ) {
        return;
      }

      event.preventDefault();
      scheduleSubmit(0);
    };

    const handleQuantityInput = (event) => {
      const target = event.target;

      if (
        !(target instanceof HTMLInputElement) ||
        target.name !== 'updates[]'
      ) {
        return;
      }

      syncDecreaseButtonState(target);

      if (!isQuantityReadyForSubmit(target)) {
        hasDeferredUpdateWhileEditing = true;
        window.clearTimeout(updateTimer);
        return;
      }

      if (target === document.activeElement) {
        hasDeferredUpdateWhileEditing = true;
        window.clearTimeout(updateTimer);
        return;
      }

      scheduleSubmit();
    };

    const handleQuantityChange = (event) => {
      const target = event.target;

      if (
        !(target instanceof HTMLInputElement) ||
        target.name !== 'updates[]'
      ) {
        return;
      }

      syncDecreaseButtonState(target);

      const rawValue = target.value.trim();

      if (rawValue === '') {
        hasDeferredUpdateWhileEditing = false;
        window.clearTimeout(updateTimer);
        return;
      }

      const parsedValue = Number.parseInt(rawValue, 10);

      if (Number.isNaN(parsedValue)) {
        hasDeferredUpdateWhileEditing = false;
        window.clearTimeout(updateTimer);
        return;
      }

      const clampedValue = clampQuantity(parsedValue, target);

      if (clampedValue !== parsedValue) {
        target.value = String(clampedValue);
        syncDecreaseButtonState(target);
      }

      hasDeferredUpdateWhileEditing = false;
      scheduleSubmit();
    };

    const handleFocusIn = (event) => {
      const target = event.target;

      if (
        !(target instanceof HTMLInputElement) ||
        target.name !== 'updates[]'
      ) {
        return;
      }

      window.clearTimeout(updateTimer);
    };

    const handleFocusOut = (event) => {
      const target = event.target;

      if (
        !(target instanceof HTMLInputElement) ||
        target.name !== 'updates[]'
      ) {
        return;
      }

      syncDecreaseButtonState(target);

      if (!hasDeferredUpdateWhileEditing) {
        return;
      }

      if (!isQuantityReadyForSubmit(target)) {
        hasDeferredUpdateWhileEditing = false;
        return;
      }

      hasDeferredUpdateWhileEditing = false;
      scheduleSubmit(0);
    };

    const handleQuantityActionClick = (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const button = target.closest(SELECTORS.quantityActionButton);

      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      const input = getQuantityInputFromActionButton(button);

      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      const action = button.dataset.quantityAction;

      if (action === 'decrease' && button.classList.contains('is-remove')) {
        input.value = '0';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        scheduleSubmit(0);
        return;
      }

      const current = Number.parseInt(input.value, 10);
      const safeCurrent = Number.isNaN(current) ? parseMin(input) : current;
      const delta = action === 'decrease' ? -1 : 1;
      const nextValue = clampQuantity(safeCurrent + delta, input);

      if (nextValue === safeCurrent) {
        return;
      }

      input.value = String(nextValue);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const handleRemoveLinkClick = (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const removeLink = target.closest(SELECTORS.removeLink);

      if (!(removeLink instanceof HTMLAnchorElement)) {
        return;
      }

      event.preventDefault();

      const cartItem = removeLink.closest(SELECTORS.cartItem);

      if (!(cartItem instanceof Element)) {
        return;
      }

      const input = cartItem.querySelector(SELECTORS.quantityInput);

      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      input.value = '0';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      scheduleSubmit(0);
    };

    initializeQuantityInputs();

    form.addEventListener('submit', handleFormSubmit);
    form.addEventListener('input', handleQuantityInput);
    form.addEventListener('change', handleQuantityChange);
    form.addEventListener('focusin', handleFocusIn);
    form.addEventListener('focusout', handleFocusOut);
    form.addEventListener('click', handleQuantityActionClick);
    form.addEventListener('click', handleRemoveLinkClick);
  };

  const setupCartAutoUpdate = (root = document) => {
    const forms = root.querySelectorAll(SELECTORS.cartForm);

    forms.forEach((form) => {
      createCartFormController(form);
    });
  };

  Object.assign(cartAutoUpdate, {
    createCartFormController,
    setupCartAutoUpdate,
  });
})();
